/**
 * A tiny event bus that lets ApiService (non-React) notify the UI
 * when the user has been logged in on another device and their session
 * was invalidated server-side (401 that survives a token refresh attempt).
 *
 * The conflict state is also persisted in localStorage so the modal
 * reappears even after a page refresh, until the user clicks OK.
 */

const SESSION_CONFLICT_EVENT = 'jsyog:session_conflict';
const SESSION_CONFLICT_STORAGE_KEY = 'jsyog:session_conflict_pending';

/** Returns true if a session conflict has been recorded (survives refresh). */
export function hasSessionConflictPending(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(SESSION_CONFLICT_STORAGE_KEY) === '1';
}

/** Clear the persisted conflict flag (call after the user clicks OK). */
export function clearSessionConflictPending(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(SESSION_CONFLICT_STORAGE_KEY);
}

/** Fire this when the server returns 401 and cannot be refreshed. */
export function emitSessionConflict(): void {
  if (typeof window === 'undefined') return;
  // Persist so the popup survives a page refresh
  localStorage.setItem(SESSION_CONFLICT_STORAGE_KEY, '1');
  window.dispatchEvent(new CustomEvent(SESSION_CONFLICT_EVENT));
}

/** Listen for the session conflict event. Returns an unsubscribe function. */
export function onSessionConflict(handler: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(SESSION_CONFLICT_EVENT, handler);
  return () => window.removeEventListener(SESSION_CONFLICT_EVENT, handler);
}
